'use client';

import React from 'react';
import dynamic from 'next/dynamic';
import { Box, CircularProgress } from '@mui/material';
import type { Marker, Arrow, RegionClickInfo } from '@/shared/ui/display/MapboxMap';
import { CounselMapSettings } from './CounselMapSettings';

const MapboxMap = dynamic(
  () => import('@/shared/ui/display/MapboxMap').then((m) => ({ default: m.MapboxMap })),
  {
    ssr: false,
    loading: () => (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 200,
          bgcolor: 'action.hover',
        }}
      >
        <CircularProgress size={32} />
      </Box>
    ),
  }
);

export interface CounselMapViewProps {
  markers: Marker[];
  arrows: Arrow[];
  /** 근무구역 경계 GeoJSON (플래너 배정 구역 등) */
  regionLayers?: GeoJSON.FeatureCollection[];
  /** 근무구역 표시명 (Chip/툴팁용, codePath 등) */
  regionLabels?: string[];
  /** 선택된 구역의 rootServiceRegionId (루트 기준으로 같은 루트만 원색, 나머지 회색) */
  selectedRootServiceRegionId?: string | null;
  /** 구역 클릭 콜백 */
  onRegionClick?: (info: RegionClickInfo | null) => void;
  minHeight?: number;
  /** 컨테이너 높이를 채우도록 flex: 1 적용 */
  fill?: boolean;
  /** 시간으로 보기 모드 (마커 배지: 상담순서→상담시간) */
  viewByTime?: boolean;
  onViewByTimeChange?: (checked: boolean) => void;
  /** 마커 클릭 콜백 */
  onMarkerClick?: (markerId: string) => void;
  /** false면 CounselMapSettings 미렌더 */
  renderSettings?: boolean;
  /** MapboxMap: 이 값이 바뀔 때만 카메라 refit (필터만 바뀌면 키 유지) */
  refitBoundsKey?: string | number;
  /** 마커 변화와 무관하게 카메라·초기 중심 고정 */
  preserveCamera?: boolean;
}

/**
 * CounselMap 순수 UI 컴포넌트
 * - 데이터 조회 로직 없음, markers/arrows/근무구역 렌더
 */
export function CounselMapView({
  markers,
  arrows,
  regionLayers,
  regionLabels,
  selectedRootServiceRegionId,
  onRegionClick,
  minHeight = 150,
  fill = false,
  viewByTime = false,
  onViewByTimeChange,
  onMarkerClick,
  renderSettings = true,
  refitBoundsKey,
  preserveCamera,
}: CounselMapViewProps) {
  return (
    <Box
      sx={{
        ...(fill ? { flex: 1, minHeight: 0 } : { flexShrink: 1, minHeight }),
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <MapboxMap
        markers={markers}
        arrows={arrows}
        regionLayers={regionLayers}
        selectedRootServiceRegionId={selectedRootServiceRegionId}
        onRegionClick={onRegionClick}
        height={fill ? '100%' : undefined}
        minHeight={fill ? undefined : minHeight}
        onMarkerClick={onMarkerClick}
        refitBoundsKey={refitBoundsKey}
        preserveCamera={preserveCamera}
      />
      {renderSettings && (
        <CounselMapSettings
          viewByTime={viewByTime}
          onViewByTimeChange={onViewByTimeChange}
          regionLabels={regionLabels}
        />
      )}
    </Box>
  );
}
